/// <reference lib="dom" />
import * as d3 from "d3";
import { pathFromPixelPoints } from "../scaleUtils.ts";
import { appendBackgroundRect, appendFixedSvg, appendScaledSvg } from "../d3Chart.ts";
import { appendPointHandle, PointHandle, PointHandleLock } from "../pointHandle.ts";
import { G } from "../graphConstants.ts";

// Shared shell for every transfer-curve chart (compressor, waveshaper, …): background + unity
// diagonal + curve path, with an optional draggable handle. A read-only preview graph is just
// this chart mounted in "inline" mode with no handle config — not a separate implementation.

// Fixed pixel size shared by every transfer-curve editor (compressor, waveshaper, …) — the
// scaled/inline preview size instead comes from each consumer's GraphOpts.
export const TransferCurveEditorSize = {
  width: 10 * G,
  height: 6 * G,
  pad: Math.round(G / 4),
};

export interface TransferCurveChartOpts {
  width: number;
  height: number;
  pad: number;
}

// Builds the unity reference diagonal path (bottom-left to top-right in SVG coords). Corner to
// corner regardless of domain, so no scale is needed — just the two pixel endpoints.
export function buildUnityPath(svgWidth: number, svgHeight: number, pad: number): string {
  return pathFromPixelPoints([
    { x: pad, y: svgHeight - pad },
    { x: svgWidth - pad, y: pad },
  ]);
}

export interface TransferCurveHandleConfig {
  color: string;
  lock?: PointHandleLock;
}

export interface TransferCurveChart {
  curvePath: d3.Selection<SVGPathElement, unknown, null, undefined>;
  handle: PointHandle | null;
}

export function initTransferCurveChart(
  container: HTMLDivElement,
  opts: TransferCurveChartOpts,
  color: string,
  mode: "editor" | "inline",
  handle?: TransferCurveHandleConfig,
): TransferCurveChart {
  const { width, height, pad } = opts;
  const svg = mode === "editor"
    ? appendFixedSvg(container, width, height)
    : appendScaledSvg(container, width, height);
  appendBackgroundRect(svg, width, height);

  svg.append("path")
    .attr("d", buildUnityPath(width, height, pad))
    .attr("stroke", "#2a2a2a")
    .attr("stroke-width", mode === "editor" ? 1 : 0.5)
    .attr("fill", "none");

  const curvePath = svg.append("path")
    .attr("fill", "none")
    .attr("stroke", color)
    .attr("stroke-width", mode === "editor" ? 1.5 : 1)
    .attr("stroke-linejoin", "round")
    .style("pointer-events", "none");

  const pointHandle = handle
    ? appendPointHandle(
      svg,
      { left: pad, top: pad, right: width - pad, bottom: height - pad },
      { color: handle.color, lock: handle.lock ?? "none", verticalLine: handle.lock === "x" },
    )
    : null;

  return { curvePath, handle: pointHandle };
}

export interface TransferCurveHandlePosition {
  x: number;
  y: number;
  label: string;
  sublabel?: string;
  onDrag: (x: number, y: number) => void;
}

export function updateTransferCurveChart(
  chart: TransferCurveChart,
  pathD: string,
  opts?: { color?: string; handle?: TransferCurveHandlePosition },
): void {
  chart.curvePath.attr("d", pathD);
  if (opts?.color !== undefined) chart.curvePath.attr("stroke", opts.color);

  if (chart.handle && opts?.handle) {
    chart.handle.setOnDrag(opts.handle.onDrag);
    chart.handle.setPosition(opts.handle.x, opts.handle.y);
    chart.handle.setLabel(opts.handle.label);
    if (opts.handle.sublabel !== undefined) chart.handle.setSublabel(opts.handle.sublabel);
  }
}
